import { useEffect, useRef } from 'react';

const DEBUG_ATTRIBUTE = 'data-debug-dom-code-nav';
const HIGHLIGHT_CLASS = 'debug-dom-code-navigation-highlight';
const SOURCE_ATTRIBUTE = 'data-debug-source';
const SOURCE_LABEL_ATTRIBUTE = 'data-debug-source-label';

type DebugSource = {
  file: string;
  line: number;
  column: number;
};

const getDomPath = (element: Element) => {
  const path: string[] = [];
  let node: Element | null = element;

  while (node) {
    let selector = node.nodeName.toLowerCase();

    if (node.id) {
      selector += `#${node.id}`;
    } else {
      const classNames = Array.from(node.classList || [])
        .filter(Boolean)
        .slice(0, 3)
        .map((name) => name.replace(/\s+/g, '.'))
        .join('.');

      if (classNames) {
        selector += `.${classNames}`;
      }
    }

    const parent = node.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter((child) => child.nodeName === node!.nodeName);
      const index = siblings.indexOf(node) + 1;
      if (index > 1) {
        selector += `:nth-of-type(${index})`;
      }
    }

    path.unshift(selector);
    node = node.parentElement;
  }

  return path.join(' > ');
};

const isCodeNavigationClick = (event: MouseEvent) => {
  return event.button === 0 && (event.altKey || (event.metaKey && event.shiftKey));
};

const getDebugSourceElement = (element: Element) => {
  return element.closest<Element>(`[${SOURCE_ATTRIBUTE}]`);
};

const parseDebugSource = (source: string): DebugSource | null => {
  const match = source.match(/^(.*):(\d+):(\d+)$/);

  if (!match) {
    return null;
  }

  return {
    file: match[1],
    line: Number(match[2]),
    column: Number(match[3])
  };
};

const openSourceInVsCode = (source: DebugSource) => {
  const encodedFile = source.file.split('/').map(encodeURIComponent).join('/');
  const editorUrl = `vscode://file${encodedFile}:${source.line}:${source.column}`;
  window.location.href = editorUrl;
};

export function useDomCodeNavigation() {
  const highlightedRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    document.documentElement.setAttribute(DEBUG_ATTRIBUTE, 'true');

    const clearHighlight = () => {
      if (highlightedRef.current) {
        highlightedRef.current.classList.remove(HIGHLIGHT_CLASS);
        highlightedRef.current = null;
      }
    };

    const handleDebugPointerDown = (event: PointerEvent) => {
      if (!isCodeNavigationClick(event)) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const sourceElement = getDebugSourceElement(target);
      const sourceValue = sourceElement?.getAttribute(SOURCE_ATTRIBUTE);

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      clearHighlight();
      sourceElement?.classList.add(HIGHLIGHT_CLASS);
      highlightedRef.current = sourceElement ?? target;

      const domPath = getDomPath(target);
      const label = sourceElement?.getAttribute(SOURCE_LABEL_ATTRIBUTE) ?? sourceValue;

      console.groupCollapsed('DOM ↔ Código');
      console.log('Elemento selecionado:', target);
      console.log('Caminho DOM:', domPath);
      if (sourceValue) {
        console.log('Origem de código:', label);
      } else {
        console.warn('Nenhuma origem de código encontrada para este elemento.');
      }
      console.groupEnd();

      if (!sourceValue) {
        return;
      }

      const source = parseDebugSource(sourceValue);

      if (!source) {
        console.warn('Origem de código inválida:', sourceValue);
        return;
      }

      openSourceInVsCode(source);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key.toLowerCase() === 'd') {
        // eslint-disable-next-line no-console
        console.info('DOM ↔ Código ativado: Alt+Click ou Cmd+Shift+Click para abrir o elemento no VS Code.');
      }
    };

    window.addEventListener('pointerdown', handleDebugPointerDown, true);
    window.addEventListener('keydown', handleKeyDown, true);

    // eslint-disable-next-line no-console
    console.info('DOM ↔ Código preparado. Use Alt+Click ou Cmd+Shift+Click em desenvolvimento para abrir elementos no VS Code.');

    return () => {
      clearHighlight();
      window.removeEventListener('pointerdown', handleDebugPointerDown, true);
      window.removeEventListener('keydown', handleKeyDown, true);
      document.documentElement.removeAttribute(DEBUG_ATTRIBUTE);
    };
  }, []);
}
